// frontend/src/utils/conversation/buildTurnSignalArbitration.js
import { getRelationshipDeclarationScoreTerms } from '../relationships/relationshipOntology';

function normalizeText(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s'",.-]/g, '');
}

function clampScore(value = 0) {
  return Math.max(0, Math.min(1, Number(Number(value || 0).toFixed(2))));
}

function countWords(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

function containsEmotionalDisclosure(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;

  const emotionalPhrases = [
    "i'm lonely",
    'im lonely',
    'i am lonely',
    'feeling lonely',
    'feel lonely',
    'a bit lonely',
    'sort of lonely',
    'kind of lonely',
    'i miss',
    'missing',
    "i'm sad",
    'im sad',
    'i am sad',
    "i'm hurting",
    'im hurting',
    'i am hurting',
    "i'm overwhelmed",
    'im overwhelmed',
    'i am overwhelmed',
    "i'm struggling",
    'im struggling',
    'i am struggling',
    "i'm upset",
    'im upset',
    'i am upset',
    "i'm worried",
    'im worried',
    'i am worried',
    "i'm anxious",
    'im anxious',
    'i am anxious',
    "i'm scared",
    'im scared',
    'i am scared',
    'wish she were around',
    'wish he were around',
    'wish they were around',
  ];

  if (emotionalPhrases.some((phrase) => lower.includes(phrase))) {
    return true;
  }

  return /\b(lonely|miss|missing|sad|hurt|hurting|overwhelmed|struggling|upset|worried|anxious|scared|grief|grieving)\b/.test(
    lower
  );
}

function containsClosureLanguage(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;

  const closureSignals = [
    'never mind',
    'nevermind',
    'leave it',
    'leave it there',
    'its ok',
    "it's ok",
    'its okay',
    "it's okay",
    'thats okay',
    "that's okay",
    'thats fine',
    "that's fine",
    'drop it',
    'forget it',
    'lets leave it',
    "let's leave it",
  ];

  return closureSignals.some((phrase) => lower === phrase || lower.includes(phrase));
}

function detectSoftDeclinePrefix(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;

  const softDeclinePrefixes = [
    'no not really',
    'not really',
    'nope not really',
    'nah not really',
    'no nothing specific',
    'nothing specific',
    'no one in particular',
    'nobody specific',
    'not anyone specific',
  ];

  return softDeclinePrefixes.some(
    (phrase) => lower === phrase || lower.startsWith(`${phrase},`) || lower.startsWith(`${phrase} `)
  );
}

function isQuestionLike(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;

  if (String(message || '').includes('?')) return true;

  return /^(what|why|how|when|where|who|can you|could you|would you|will you|do you|did you|are you|is it)\b/.test(
    lower
  );
}


function containsNewInformationRequest(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;

  if (String(message || '').includes('?')) return true;

  return /\b(what|why|how|when|where|who|can you|could you|would you|will you|do you|did you|are you|is it)\b/.test(
    lower
  );
}

function hasExplicitObjectiveExit(objectiveDecisionState = {}) {
  return (
    objectiveDecisionState?.signals?.objectiveExited === true ||
    objectiveDecisionState?.detectedObjectiveExit?.explicit === true
  );
}

function computePendingAnswerScore(pendingQuestionState = {}) {
  if (pendingQuestionState?.pendingAuthorityRevoked === true) {
    return 0;
  }

  if (!pendingQuestionState?.detectedAnswerToPendingQuestion) {
    return 0;
  }

  const signal = pendingQuestionState?.answerSignal || 'none';

  if (signal === 'substantive') return 0.72;
  if (signal === 'low') return 0.38;
  return 0.2;
}

function computeTargetShiftScore(pendingQuestionState = {}) {
  if (pendingQuestionState?.pendingAuthorityRevoked === true) {
    return clampScore(pendingQuestionState?.targetShiftScore || 0);
  }

  return clampScore(pendingQuestionState?.targetShiftScore || 0);
}

function computeRelationshipDeclarationScore(message = '') {
  const lower = normalizeText(message);
  if (!lower) return 0;

  const relationshipGroup = getRelationshipDeclarationScoreTerms()
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  const patterns = [
    new RegExp(`\\b[a-z][a-z' -]{0,40} is my (${relationshipGroup})\\b`, 'i'),
    new RegExp(`\\bmy (${relationshipGroup}) is [a-z][a-z' -]{0,40}\\b`, 'i'),
  ];

  if (patterns.some((p) => p.test(lower))) {
    return 0.9;
  }

  return 0;
}

function computeSelectionDeclineScore({
  pendingSelectionSignals = {},
  pendingQuestionState = {},
  message = '',
} = {}) {
  const lower = normalizeText(message);
  const hasPendingQuestion = !!pendingQuestionState?.pendingQuestion;
  const noSpecificSelectionLike = pendingSelectionSignals?.noSpecificSelectionLike === true;
  const closureLike = pendingSelectionSignals?.closureLike === true;
  const greetingLike = pendingSelectionSignals?.greetingLike === true;
  const softDeclinePrefix = detectSoftDeclinePrefix(lower);
  const pendingAuthorityRevoked = pendingQuestionState?.pendingAuthorityRevoked === true;

  if (greetingLike) return 0;
  if (!hasPendingQuestion) return 0;
  if (pendingAuthorityRevoked) return 0;

  let score = 0;

  if (noSpecificSelectionLike) score += 0.45;
  if (softDeclinePrefix) score += 0.2;
  if (closureLike) score += 0.15;

  return clampScore(score);
}

function computeClosureScore({
  pendingSelectionSignals = {},
  pendingQuestionState = {},
  message = '',
} = {}) {
  const lower = normalizeText(message);
  const closureLike = pendingSelectionSignals?.closureLike === true;
  const lowSignalAnswer = pendingQuestionState?.answerSignal === 'low';
  const pendingAuthorityRevoked = pendingQuestionState?.pendingAuthorityRevoked === true;

  if (pendingAuthorityRevoked) {
    return 0;
  }

  let score = 0;

  if (closureLike) score += 0.7;
  if (containsClosureLanguage(lower)) score += 0.2;
  if (lowSignalAnswer) score += 0.08;

  return clampScore(score);
}

function computeEmotionalDisclosureScore({
  message = '',
  pendingQuestionState = {},
} = {}) {
  const lower = normalizeText(message);
  const words = countWords(lower);
  const answerSignal = pendingQuestionState?.answerSignal || 'none';

  if (!lower) return 0;

  let score = 0;

  if (containsEmotionalDisclosure(lower)) score += 0.72;
  if (/\b(i am|i'm|im|i feel|i felt|ive been|i've been|i was)\b/.test(lower)) score += 0.08;
  if (words >= 6) score += 0.05;
  if (answerSignal === 'substantive') score += 0.05;

  return clampScore(score);
}

function computeEntityActivationScore({
  message = '',
  referenceState = {},
  turnAttachment = {},
  currentEntity = null,
} = {}) {
  const lower = normalizeText(message);

  if (!lower) return 0;

  const resolvedEntity =
    turnAttachment?.resolvedEntity ||
    referenceState?.lastReferencedEntity ||
    currentEntity ||
    null;

  if (!resolvedEntity?.name) return 0;

  let score = 0.5;

  const normalizedName = normalizeText(resolvedEntity.name);
  if (normalizedName && lower.includes(normalizedName)) {
    score += 0.25;
  }

  if (resolvedEntity?.role) {
    score += 0.08;
  }

  if (
    resolvedEntity?.source === 'explicit_name_match' ||
    resolvedEntity?.source === 'relationship_group_match'
  ) {
    score += 0.07;
  }

  if (resolvedEntity?.type === 'relationship_group') {
    score += 0.12;
  }

  return clampScore(score);
}

function computePronounResolutionScore(pronounResolutionSignal = null) {
  if (!pronounResolutionSignal?.detected) return 0;
  return clampScore(pronounResolutionSignal?.score || 0);
}

function computeContextualContinuationScore({
  message = '',
  referenceState = {},
  turnAttachment = {},
  currentEntity = null,
  pronounResolutionSignal = null,
  pendingQuestionState = {},
} = {}) {
  const lower = normalizeText(message);

  if (!lower) return 0;
  if (isQuestionLike(message)) return 0;
  if (computeRelationshipDeclarationScore(lower) > 0) return 0;
  if (pendingQuestionState?.detectedAnswerToPendingQuestion === true) return 0;
  if (pendingQuestionState?.pendingAuthorityRevoked === true) return 0;

  const resolvedEntity =
    turnAttachment?.resolvedEntity ||
    referenceState?.lastReferencedEntity ||
    currentEntity ||
    null;

  if (!resolvedEntity?.name) return 0;

  let score = 0.4;

  const normalizedName = normalizeText(resolvedEntity.name);

  if (normalizedName && lower.includes(normalizedName)) {
    score += 0.15;
  }

  if (pronounResolutionSignal?.detected === true) {
    score += 0.15;
  }

  if (/\b(i|im|i'm|i am|we|we're|we are)\b/.test(lower)) {
    score += 0.05;
  }

  if (countWords(lower) >= 6) {
    score += 0.05;
  }

  if (
    /\b(far|away|distance|live|lives|talk|talks|family|comfort|hard|walk|park|river)\b/.test(
      lower
    )
  ) {
    score += 0.1;
  }

  return clampScore(score);
}

function computeTopicContinuationScore({
  referenceState = {},
  pendingQuestionState = {},
  message = '',
} = {}) {
  const lower = normalizeText(message);

  if (!lower) return 0;
  if (isQuestionLike(message)) return 0;
  if (pendingQuestionState?.pendingAuthorityRevoked === true) return 0;
  if (pendingQuestionState?.detectedAnswerToPendingQuestion === true) return 0;

  const topicContinuity = referenceState?.topicContinuity || null;
  const hasTopic = !!referenceState?.lastReferencedTopic?.topic;
  const hasThread = !!referenceState?.lastReferencedThread?.thread;

  if (!hasTopic && !hasThread) return 0;

  let score = 0;

  if (topicContinuity?.shouldCarryForward === true) score += 0.58;
  if (topicContinuity?.continuationLikely === true) score += 0.18;
  if (topicContinuity?.clarificationLikely === true) score += 0.16;
  if (topicContinuity?.topicStable === true) score += 0.08;

  if (topicContinuity?.detectedShift === true) {
    score -= 0.5;
  }

  return clampScore(score);
}

function computeReasoningContinuationScore({
  message = '',
  objectiveDecisionState = {},
  pendingQuestionState = {},
} = {}) {
  const lower = normalizeText(message);

  if (!lower) return 0;
  if (isQuestionLike(message)) return 0;
  if (pendingQuestionState?.pendingAuthorityRevoked === true) return 0;
  if (pendingQuestionState?.detectedAnswerToPendingQuestion === true) return 0;
  if (!objectiveDecisionState?.activeObjective && !objectiveDecisionState?.activeDecision) return 0;

  let score = 0;

  if (objectiveDecisionState?.detectedEvidenceFragment) score += 0.72;
  if (objectiveDecisionState?.signals?.carriedForward === true) score += 0.12;
  if ((objectiveDecisionState?.activeReasoningChain || []).length > 0) score += 0.08;
  if (/\b(so|then|therefore|meaning|which means|that means|that changes)\b/.test(lower)) {
    score += 0.08;
  }

  return clampScore(score);
}

function computeDecisionSupportScore({
  message = '',
  objectiveDecisionState = {},
  pendingQuestionState = {},
} = {}) {
  const lower = normalizeText(message);

  if (!lower) return 0;
  if (pendingQuestionState?.pendingAuthorityRevoked === true) return 0;
  if (!objectiveDecisionState?.activeDecision) return 0;

  let score = 0;

  if (objectiveDecisionState?.detectedDecisionRequest) score += 0.76;
  if (/\b(should i|should we|worth|worthwhile|good idea|bad idea|decide|decision)\b/.test(lower)) {
    score += 0.12;
  }
  if (objectiveDecisionState?.signals?.carriedForward === true) score += 0.08;

  return clampScore(score);
}

function computeObjectiveContinuationScore({
  objectiveDecisionState = {},
  referenceState = {},
  pendingQuestionState = {},
} = {}) {
  if (pendingQuestionState?.pendingAuthorityRevoked === true) return 0;
  if (!objectiveDecisionState?.activeObjective) return 0;

  let score = 0.34;

  if (objectiveDecisionState?.signals?.carriedForward === true) score += 0.16;
  if (objectiveDecisionState?.signals?.inferredFromTopic === true) score += 0.1;
  if (referenceState?.topicContinuity?.shouldCarryForward === true) score += 0.1;
  if ((objectiveDecisionState?.activeReasoningChain || []).length > 0) score += 0.08;

  return clampScore(score);
}

function computeFramePersistenceScore({
  referenceState = {},
  currentEntity = null,
} = {}) {
  let score = 0;

  if (referenceState?.lastReferencedEntity?.name || currentEntity?.name) {
    score += 0.42;
  }

  if (referenceState?.lastReferencedTopic?.topic) {
    score += 0.24;
  }

  if (referenceState?.lastReferencedThread?.thread) {
    score += 0.14;
  }

  if (referenceState?.topicContinuity?.topicStable === true) {
    score += 0.12;
  }

  if (referenceState?.topicContinuity?.shouldCarryForward === true) {
    score += 0.08;
  }

  return clampScore(score);
}

function computeFrameShiftPressureScore({
  message = '',
  scores = {},
  referenceState = {},
  pendingQuestionState = {},
} = {}) {
  const lower = normalizeText(message);
  if (!lower) return 0;

  let score = 0;

  if (pendingQuestionState?.pendingAuthorityRevoked === true) {
    score += 0.7;
  }

  if ((pendingQuestionState?.targetShiftScore || 0) >= 0.6) {
    score += 0.2;
  }

  if (referenceState?.topicContinuity?.detectedShift === true) {
    score += 0.45;
  }

  if (scores.relationshipDeclaration >= 0.85) {
    score += 0.25;
  }

  if (isQuestionLike(lower) && countWords(lower) >= 6) {
    score += 0.1;
  }

  if (countWords(lower) >= 10 && scores.topicContinuation < 0.35) {
    score += 0.1;
  }

  return clampScore(score);
}

function getDominantSignal(scores = {}) {
  const ordered = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [winnerKey, winnerScore] = ordered[0] || ['none', 0];

  if (!winnerScore || winnerScore <= 0) {
    return {
      winner: 'none',
      winnerScore: 0,
      runnerUp: null,
      runnerUpScore: 0,
      margin: 0,
    };
  }

  const [runnerUpKey, runnerUpScore] = ordered[1] || [null, 0];

  return {
    winner: winnerKey,
    winnerScore,
    runnerUp: runnerUpKey,
    runnerUpScore,
    margin: clampScore(winnerScore - runnerUpScore),
  };
}

function buildReplyMode({
  dominantSignal = 'none',
  scores = {},
} = {}) {
  if (dominantSignal === 'objectiveExit') {
    return 'follow_new_target';
  }

  if (dominantSignal === 'targetShift') {
    return 'follow_new_target';
  }

  if (dominantSignal === 'pronounResolution') {
    return 'preserve_continuity';
  }

  if (dominantSignal === 'topicContinuation') {
    return 'preserve_topic_continuity';
  }

  if (dominantSignal === 'contextualContinuation') {
    return 'contextual_acknowledgment';
  }

  if (dominantSignal === 'reasoningContinuation') {
    return 'advance_reasoning_chain';
  }

  if (dominantSignal === 'decisionSupport') {
    return 'support_active_decision';
  }

  if (dominantSignal === 'objectiveContinuation') {
    return 'support_active_objective';
  }

  if (
    dominantSignal === 'emotionalDisclosure' ||
    (scores.emotionalDisclosure >= 0.7 && scores.entityActivation >= 0.6)
  ) {
    return 'acknowledge_and_followup';
  }

  if (dominantSignal === 'closure') {
    return 'close_or_redirect';
  }

  if (
    dominantSignal === 'selectionDecline' &&
    scores.emotionalDisclosure < 0.55 &&
    scores.entityActivation < 0.55
  ) {
    return 'close_or_redirect';
  }

  if (dominantSignal === 'pendingAnswer') {
    return 'answer_pending_question';
  }

  return 'default';
}

function buildTurnSignalArbitration({
  message = '',
  pendingQuestionState = null,
  pendingSelectionSignals = null,
  referenceState = null,
  turnAttachment = null,
  currentEntity = null,
  pronounResolutionSignal = null,
  objectiveDecisionState = null,
} = {}) {
  const normalizedMessage = normalizeText(message);

  const scores = {
    pendingAnswer: computePendingAnswerScore(pendingQuestionState),
    targetShift: computeTargetShiftScore(pendingQuestionState),
    selectionDecline: computeSelectionDeclineScore({
      pendingSelectionSignals,
      pendingQuestionState,
      message: normalizedMessage,
    }),
    closure: computeClosureScore({
      pendingSelectionSignals,
      pendingQuestionState,
      message: normalizedMessage,
    }),
    emotionalDisclosure: computeEmotionalDisclosureScore({
      message: normalizedMessage,
      pendingQuestionState,
    }),
    entityActivation: computeEntityActivationScore({
      message: normalizedMessage,
      referenceState,
      turnAttachment,
      currentEntity,
    }),
    pronounResolution: computePronounResolutionScore(pronounResolutionSignal),
    contextualContinuation: computeContextualContinuationScore({
      message: normalizedMessage,
      referenceState,
      turnAttachment,
      currentEntity,
      pronounResolutionSignal,
      pendingQuestionState,
    }),
    topicContinuation: computeTopicContinuationScore({
      referenceState,
      pendingQuestionState,
      message: normalizedMessage,
    }),
    reasoningContinuation: computeReasoningContinuationScore({
      message: normalizedMessage,
      objectiveDecisionState,
      pendingQuestionState,
    }),
    decisionSupport: computeDecisionSupportScore({
      message: normalizedMessage,
      objectiveDecisionState,
      pendingQuestionState,
    }),
    objectiveContinuation: computeObjectiveContinuationScore({
      objectiveDecisionState,
      referenceState,
      pendingQuestionState,
    }),
    relationshipDeclaration: computeRelationshipDeclarationScore(normalizedMessage),
  };

  const objectiveExitWithNewRequest =
    hasExplicitObjectiveExit(objectiveDecisionState) &&
    containsNewInformationRequest(message);

  if (objectiveExitWithNewRequest) {
    scores.selectionDecline = 0;
    scores.closure = 0;
    scores.topicContinuation = 0;
    scores.reasoningContinuation = 0;
    scores.decisionSupport = 0;
    scores.objectiveContinuation = 0;
  }

  const framePersistence = computeFramePersistenceScore({
    referenceState,
    currentEntity,
  });

  const frameShiftPressure = computeFrameShiftPressureScore({
    message: normalizedMessage,
    scores,
    referenceState,
    pendingQuestionState,
  });

  const dominance = getDominantSignal(scores);

  const targetShiftOverride =
    pendingQuestionState?.pendingAuthorityRevoked === true &&
    scores.targetShift >= scores.pendingAnswer &&
    scores.targetShift >= scores.selectionDecline &&
    scores.targetShift >= scores.closure;

  const pronounOverride =
    !targetShiftOverride &&
    scores.pronounResolution >= 0.75 &&
    scores.pronounResolution > scores.entityActivation &&
    scores.pronounResolution >= scores.selectionDecline &&
    scores.pronounResolution >= scores.closure &&
    frameShiftPressure < framePersistence;

  const topicOverride =
    !targetShiftOverride &&
    !pronounOverride &&
    scores.topicContinuation >= 0.6 &&
    scores.topicContinuation >= scores.pendingAnswer &&
    scores.topicContinuation >= scores.selectionDecline &&
    scores.topicContinuation >= scores.closure &&
    scores.topicContinuation >= scores.relationshipDeclaration &&
    scores.topicContinuation >= scores.contextualContinuation &&
    scores.topicContinuation >= scores.reasoningContinuation &&
    scores.topicContinuation >= scores.decisionSupport &&
    frameShiftPressure <= framePersistence + 0.05;

  const contextualOverride =
    !targetShiftOverride &&
    !pronounOverride &&
    !topicOverride &&
    scores.contextualContinuation >= 0.65 &&
    scores.contextualContinuation >= scores.pendingAnswer &&
    scores.contextualContinuation >= scores.selectionDecline &&
    scores.contextualContinuation >= scores.closure &&
    scores.contextualContinuation >= scores.relationshipDeclaration &&
    frameShiftPressure < framePersistence;

  const reasoningOverride =
    !targetShiftOverride &&
    !pronounOverride &&
    !topicOverride &&
    !contextualOverride &&
    scores.reasoningContinuation >= 0.72 &&
    scores.reasoningContinuation >= scores.pendingAnswer &&
    scores.reasoningContinuation >= scores.selectionDecline &&
    scores.reasoningContinuation >= scores.closure &&
    scores.reasoningContinuation >= scores.relationshipDeclaration;

  const decisionOverride =
    !targetShiftOverride &&
    !pronounOverride &&
    !topicOverride &&
    !contextualOverride &&
    !reasoningOverride &&
    scores.decisionSupport >= 0.76 &&
    scores.decisionSupport >= scores.pendingAnswer &&
    scores.decisionSupport >= scores.selectionDecline &&
    scores.decisionSupport >= scores.closure &&
    scores.decisionSupport >= scores.relationshipDeclaration;

  const emotionalOverride =
    !targetShiftOverride &&
    !pronounOverride &&
    !topicOverride &&
    !contextualOverride &&
    !reasoningOverride &&
    !decisionOverride &&
    scores.emotionalDisclosure >= 0.7 &&
    (scores.entityActivation >= 0.6 ||
      scores.pendingAnswer >= 0.65 ||
      scores.pronounResolution >= 0.7);

  const dominantSignal = objectiveExitWithNewRequest
    ? 'objectiveExit'
    : targetShiftOverride
      ? 'targetShift'
      : pronounOverride
      ? 'pronounResolution'
      : topicOverride
        ? 'topicContinuation'
        : contextualOverride
          ? 'contextualContinuation'
          : reasoningOverride
            ? 'reasoningContinuation'
            : decisionOverride
              ? 'decisionSupport'
              : emotionalOverride
                ? 'emotionalDisclosure'
                : dominance.winner;

  const dominantTurnMode = objectiveExitWithNewRequest
    ? 'objective_exit'
    : targetShiftOverride
      ? 'target_shift'
      : pronounOverride
      ? 'pronoun_continuity'
      : topicOverride
        ? 'topic_continuation'
        : contextualOverride
          ? 'contextual_continuation'
          : reasoningOverride
            ? 'reasoning_continuation'
            : decisionOverride
              ? 'decision_support'
              : emotionalOverride
                ? 'emotional_disclosure'
                : dominance.winner === 'closure'
              ? 'closure'
              : dominance.winner === 'selectionDecline'
                ? 'selection_decline'
                : dominance.winner === 'pendingAnswer'
                  ? 'pending_answer'
                  : dominance.winner === 'entityActivation'
                    ? 'entity_activation'
                    : dominance.winner === 'relationshipDeclaration'
                      ? 'relationship_declaration'
                      : dominance.winner === 'reasoningContinuation'
                        ? 'reasoning_continuation'
                        : dominance.winner === 'decisionSupport'
                          ? 'decision_support'
                          : dominance.winner === 'objectiveContinuation'
                            ? 'objective_continuation'
                            : 'default';

  const replyMode = buildReplyMode({
    dominantSignal,
    scores,
  });

  const suppressNoSpecificSelectionReply =
    objectiveExitWithNewRequest ||
    targetShiftOverride ||
    pronounOverride ||
    topicOverride ||
    contextualOverride ||
    reasoningOverride ||
    decisionOverride ||
    emotionalOverride ||
    (scores.emotionalDisclosure > scores.selectionDecline &&
      scores.emotionalDisclosure >= 0.58);

  const suppressClosureReply =
    objectiveExitWithNewRequest ||
    targetShiftOverride ||
    pronounOverride ||
    topicOverride ||
    contextualOverride ||
    reasoningOverride ||
    decisionOverride ||
    emotionalOverride ||
    (scores.closure > 0 && scores.emotionalDisclosure >= 0.58);

  const shouldClosePendingQuestion =
    objectiveExitWithNewRequest ||
    pendingQuestionState?.pendingAuthorityRevoked === true ||
    pendingQuestionState?.closePendingQuestion === true ||
    scores.pendingAnswer >= 0.65 ||
    emotionalOverride ||
    scores.closure >= 0.7;

  const shouldCarryForwardEntity =
    targetShiftOverride ||
    pronounOverride ||
    contextualOverride ||
    scores.pronounResolution >= 0.7 ||
    scores.entityActivation >= 0.6 ||
    emotionalOverride;

  const shouldPrioritizeEmotionalReply =
    emotionalOverride || dominantTurnMode === 'emotional_disclosure';

  const shouldPreservePronounContinuity =
    pronounOverride || contextualOverride || scores.pronounResolution >= 0.75;

  const shouldPreserveTopicContinuity =
    topicOverride || reasoningOverride || decisionOverride || scores.topicContinuation >= 0.6;

  const isResolvedEntity =
    currentEntity?.signals?.resolved === true ||
    turnAttachment?.resolvedEntity?.signals?.resolved === true;

  const shouldBypassFallback =
    objectiveExitWithNewRequest ||
    isResolvedEntity ||
    shouldPreserveTopicContinuity ||
    (pronounResolutionSignal?.shouldBypassFallback === true &&
      shouldPreservePronounContinuity) ||
    contextualOverride ||
    reasoningOverride ||
    decisionOverride;

  const reasons = [];

  if (scores.pendingAnswer > 0) {
    reasons.push(`pending answer score ${scores.pendingAnswer}`);
  }

  if (scores.targetShift > 0) {
    reasons.push(`target shift score ${scores.targetShift}`);
  }

  if (scores.selectionDecline > 0) {
    reasons.push(`selection decline score ${scores.selectionDecline}`);
  }

  if (scores.closure > 0) {
    reasons.push(`closure score ${scores.closure}`);
  }

  if (scores.emotionalDisclosure > 0) {
    reasons.push(`emotional disclosure score ${scores.emotionalDisclosure}`);
  }

  if (scores.entityActivation > 0) {
    reasons.push(`entity activation score ${scores.entityActivation}`);
  }

  if (scores.pronounResolution > 0) {
    reasons.push(`pronoun resolution score ${scores.pronounResolution}`);
  }

  if (scores.contextualContinuation > 0) {
    reasons.push(`contextual continuation score ${scores.contextualContinuation}`);
  }

  if (scores.topicContinuation > 0) {
    reasons.push(`topic continuation score ${scores.topicContinuation}`);
  }

  if (scores.reasoningContinuation > 0) {
    reasons.push(`reasoning continuation score ${scores.reasoningContinuation}`);
  }

  if (scores.decisionSupport > 0) {
    reasons.push(`decision support score ${scores.decisionSupport}`);
  }

  if (scores.objectiveContinuation > 0) {
    reasons.push(`objective continuation score ${scores.objectiveContinuation}`);
  }

  if (scores.relationshipDeclaration > 0) {
    reasons.push(`relationship declaration score ${scores.relationshipDeclaration}`);
  }

  reasons.push(`frame persistence ${framePersistence}`);
  reasons.push(`frame shift pressure ${frameShiftPressure}`);

  if (targetShiftOverride) {
    reasons.push('target shift override applied');
  }

  if (pronounOverride) {
    reasons.push('pronoun continuity override applied');
  }

  if (topicOverride) {
    reasons.push('topic continuity override applied');
  }

  if (contextualOverride) {
    reasons.push('contextual continuation override applied');
  }

  if (reasoningOverride) {
    reasons.push('reasoning continuation override applied');
  }

  if (decisionOverride) {
    reasons.push('decision support override applied');
  }

  if (emotionalOverride) {
    reasons.push('emotional override applied');
  }

  if (objectiveExitWithNewRequest) {
    reasons.push('explicit objective exit with new information request');
  }

  return {
    signals: {
      detectedAnswerToPendingQuestion:
        pendingQuestionState?.detectedAnswerToPendingQuestion === true,
      answerSignal: pendingQuestionState?.answerSignal || 'none',
      greetingLike: pendingSelectionSignals?.greetingLike === true,
      closureLike: pendingSelectionSignals?.closureLike === true,
      noSpecificSelectionLike: pendingSelectionSignals?.noSpecificSelectionLike === true,
      extractedCandidate: pendingSelectionSignals?.extractedCandidate || null,
      emotionalDisclosureDetected: containsEmotionalDisclosure(normalizedMessage),
      contextualContinuationDetected: scores.contextualContinuation >= 0.65,
      topicContinuationDetected: scores.topicContinuation >= 0.6,
      reasoningContinuationDetected: scores.reasoningContinuation >= 0.72,
      decisionSupportDetected: scores.decisionSupport >= 0.76,
      objectiveContinuationDetected: scores.objectiveContinuation >= 0.6,
      objectiveExitWithNewRequest,
      activeObjective: objectiveDecisionState?.activeObjective || null,
      activeDecision: objectiveDecisionState?.activeDecision || null,
      activeReasoningChain: objectiveDecisionState?.activeReasoningChain || [],
      activeEntity:
        turnAttachment?.resolvedEntity ||
        referenceState?.lastReferencedEntity ||
        currentEntity ||
        null,
      activeTopic: referenceState?.lastReferencedTopic || null,
      activeThread: referenceState?.lastReferencedThread || null,
      pronouns: pronounResolutionSignal?.pronouns || [],
      pronounDetected: pronounResolutionSignal?.detected === true,
      pronounResolved: pronounResolutionSignal?.resolved === true,
      pendingAuthorityRevoked: pendingQuestionState?.pendingAuthorityRevoked === true,
      targetShift: pendingQuestionState?.targetShift || null,
      topicContinuity: referenceState?.topicContinuity || null,
      framePersistence,
      frameShiftPressure,
    },
    scores,
    dominantSignal,
    dominantSignalScore:
      dominantSignal === 'objectiveExit'
        ? 0.99
        : dominantSignal === 'targetShift'
        ? scores.targetShift
        : dominantSignal === 'pronounResolution'
          ? scores.pronounResolution
          : dominantSignal === 'topicContinuation'
            ? scores.topicContinuation
            : dominantSignal === 'contextualContinuation'
              ? scores.contextualContinuation
              : dominantSignal === 'reasoningContinuation'
                ? scores.reasoningContinuation
                : dominantSignal === 'decisionSupport'
                  ? scores.decisionSupport
                  : dominantSignal === 'objectiveContinuation'
                    ? scores.objectiveContinuation
                    : dominantSignal === 'emotionalDisclosure'
                      ? scores.emotionalDisclosure
                      : dominance.winnerScore,
    runnerUpSignal: dominance.runnerUp,
    runnerUpSignalScore: dominance.runnerUpScore,
    scoreMargin: dominance.margin,
    dominantTurnMode,
    replyMode,
    control: {
      suppressNoSpecificSelectionReply,
      suppressClosureReply,
      shouldClosePendingQuestion,
      shouldCarryForwardEntity,
      shouldPrioritizeEmotionalReply,
      shouldPreservePronounContinuity,
      shouldPreserveTopicContinuity,
      shouldBypassFallback,
    },
    pronounResolutionSignal,
    objectiveDecisionState,
    reasons,
  };
}

export {
  buildTurnSignalArbitration,
  containsEmotionalDisclosure,
};
